// eslint-disable-next-line
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { type ComponentProps, type MouseEvent, Fragment } from 'react';

import { ChevronLeftIcon } from '~components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '~components/icons/ChevronRightIcon';
import { DetailModalPanel } from '~modules/media-library/view/DetailModal/DetailModalPanel';
import noop from '~utils/noop';

type DetailModalProps = Resolve<
  Pick<ComponentProps<typeof DetailModalPanel>, 'asset' | 'AssetActions'> & {
    isOpen: boolean;
    onClose: () => void;
    onNext?: (_evt?: MouseEvent<HTMLElement>) => void;
    onPrev?: (_evt?: MouseEvent<HTMLElement>) => void;
  }
>;

export function DetailModal({ isOpen, onClose, onNext, onPrev, ...PanelProps }: DetailModalProps) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={noop} as="div" className="relative z-40">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-50"
          leave="ease-in duration-200"
          leaveFrom="opacity-50"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black opacity-50" />
        </TransitionChild>
        <div className="fixed inset-0 p-8 flex justify-center items-start overflow-hidden" onClick={onClose}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300 delay-100"
            enterFrom="opacity-0 scale-50 translate-y-4"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-50 translate-y-4"
          >
            <div className="absolute w-full max-w-md h-32 transition-all transform">
              {Boolean(onPrev) ? (
                <div className="absolute right-full top-0 bottom-0 flex items-center pt-64">
                  <div onClick={onPrev} className="w-8 h-8 m-3 text-white cursor-pointer">
                    <ChevronLeftIcon />
                  </div>
                </div>
              ) : null}
              {Boolean(onNext) ? (
                <div className="absolute left-full top-0 bottom-0 flex items-center pt-64">
                  <div onClick={onNext} className="w-8 h-8 m-3 text-white cursor-pointer">
                    <ChevronRightIcon />
                  </div>
                </div>
              ) : null}
            </div>
          </TransitionChild>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95 translate-y-4"
          >
            <DialogPanel as={DetailModalPanel} {...PanelProps} onClose={onClose} className="transition-all transform" />
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
